import { GeckoMeasurement } from 'types'
import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { parseDate } from 'utils/parsingHelpers'
import {
  optionalDateStringWithRegex,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoPipeWrapJSON {
  type: GeckoFeatureModelType.PipeWrap
  name: string
  feature_geometry: {
    y_start: number
    y_end: number
  }
  installation_date: string | undefined
}

export const NewGeckoPipeWrapJSON: GeckoPipeWrapJSON = {
  type: GeckoFeatureModelType.PipeWrap,
  name: 'New Pipe Wrap',
  feature_geometry: {
    y_start: 0,
    y_end: 0,
  },
  installation_date: undefined,
}

export class GeckoPipeWrap extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.PipeWrap
  readonly geometry: GeckoComponentRect
  readonly installationDate: Date | undefined

  /**
   * Private constructor, allowing for a factory method that can return undefined
   * @param id
   * @param name
   * @param feature_geometry
   * @param installationDate
   * @private
   */
  constructor(
    id: string,
    name: string,
    geometry: GeckoComponentRect,
    installationDate: Date | undefined
  ) {
    // TODO: Maybe we need to change this?
    super(id)
    this.name = name
    this.geometry = geometry
    this.installationDate = installationDate
  }

  public static CreateFromJSON(
    componentID: string,
    wrapID: string,
    wrapJson: GeckoPipeWrapJSON,
    componentGeometry: GeckoComponentRect
  ): GeckoPipeWrap {
    const name = wrapJson.name

    const featureGeometryData = wrapJson.feature_geometry as Record<
      string,
      number
    >

    const wrapGeometry = GeckoComponentRect.CreateFromXYs(
      componentID as string,
      componentGeometry.x.start.num,
      featureGeometryData.y_start,
      componentGeometry.x.end.num,
      featureGeometryData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const wrapInstallationDate = wrapJson.installation_date
      ? parseDate(wrapJson.installation_date)
      : undefined

    return new GeckoPipeWrap(wrapID, name, wrapGeometry, wrapInstallationDate)
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordJson: Record<string, GeckoPipeWrapJSON>,
    componentGeometry: GeckoComponentRect
  ): Map<string, GeckoPipeWrap> {
    return Object.keys(recordJson).reduce((resultObj, wrapID) => {
      resultObj.set(
        wrapID,
        GeckoPipeWrap.CreateFromJSON(
          componentID,
          wrapID,
          recordJson[wrapID],
          componentGeometry
        )
      )
      return resultObj
    }, new Map<string, GeckoPipeWrap>())
  }

  asJSON() {
    return generatePipeWrap(
      this.name,
      this.geometry.y.start,
      this.geometry.y.end,
      this.installationDate
    )
  }
}

export function generatePipeWrap(
  name: string,
  y_start: GeckoMeasurement,
  y_end: GeckoMeasurement,
  installationDate?: Date
): GeckoPipeWrapJSON {
  const yStartInMeters = y_start.convertTo(GeckoMeasurementUnit.Meters)
  const yEndInMeters = y_end.convertTo(GeckoMeasurementUnit.Meters)

  const pipeWrapJSON: GeckoPipeWrapJSON = {
    type: GeckoFeatureModelType.PipeWrap,
    name: name,
    installation_date: installationDate
      ? installationDate.toISOString().split('T')[0]
      : undefined,
    feature_geometry: {
      y_start: yStartInMeters.num,
      y_end: yEndInMeters.num,
    },
  }
  return pipeWrapJSON
}

export const PipeWrapFormFields = (pipeWrapJSON: GeckoPipeWrapJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Name',
      value: pipeWrapJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'installation_date',
      label: 'Installation Date',
      value: pipeWrapJSON.installation_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Date,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: pipeWrapJSON.feature_geometry.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: pipeWrapJSON.feature_geometry.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const PipeWrapJSONFromFormFields = (formFields: FormField[]) => {
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )

  return generatePipeWrap(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      GeckoMeasurementUnit.Meters
    ),

    installationDate?.value !== undefined
      ? new Date(installationDate?.value as string)
      : undefined
  )
}

export const PipeWrapFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.PipeWrap,
  title: 'Pipe Wraps',
  singularName: 'Pipe Wrap',
  fields: ['name', 'y_start', 'y_end'],
  sortBy: [{ fieldName: 'y_start', order: 'asc' }],
}
